import { Theme, css } from '@emotion/react'
import styled from '@emotion/styled'
import Link from 'next/link'
import React from 'react'

import Badge from './Badge'
import BetaBadge from './BetaBadge'
import { ArrowRightIcon, CaretLeftIcon } from './icons'
import useTemplateId from 'hooks/useTemplateId'
import { AdvancedSettingsRoutes, CustomizeLookRoutes, WidgetContentRoutes } from 'layouts/routes'

type AllowedInternalRoutes = WidgetContentRoutes | CustomizeLookRoutes | AdvancedSettingsRoutes

type Props = {
	href?: AllowedInternalRoutes | `${AllowedInternalRoutes}?${string}`
	children: React.ReactNode
	rightAdornment?: React.ReactNode
	icon?: React.ReactNode
	isComingSoon?: boolean
	isBeta?: boolean
	disabled?: boolean
	dataTestId?: string
	onClick?: () => void
}

const linkBaseStyles = (theme: Theme) => css`
	${theme.typography.paragraphMedium};
	text-decoration: none;
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: ${theme.spaces.space4};
	border-radius: ${theme.borderRadiuses.lg};
	margin: 0 ${theme.spaces.space1};
`

const InternalLink = styled(Link)`
	${({ theme }) => linkBaseStyles(theme)};
	color: ${({ theme }) => theme.colors.light.primaryTextColor};
	background-color: ${({ theme }) => theme.colors.light.floatSurface};
	border: 1px solid ${({ theme }) => theme.colors.light.borderColor};
	&:hover {
		background-color: ${({ theme }) => theme.colors.light.borderColor};
	}
`

const InternalLinkWrapper = styled.div`
	position: relative;
`

const ExternalLink = styled.a<{ isExternal?: boolean }>`
	${({ theme }) => linkBaseStyles(theme)};
	color: ${({ theme }) => theme.colors.light.alternativeTextColor};
	background-color: ${({ theme }) => theme.colors.light.surfaceAlternative};
	border: 1px solid ${({ theme }) => theme.colors.light.surfaceAlternative};
	cursor: pointer;
`

const DisabledLink = styled.div`
	${({ theme }) => linkBaseStyles(theme)};
	color: ${({ theme }) => theme.colors.light.alternativeTextColor};
	background-color: ${({ theme }) => theme.colors.light.surfaceAlternative};
	border: 1px solid ${({ theme }) => theme.colors.light.surfaceAlternative};
	cursor: not-allowed;
`

const SideChevron = styled(CaretLeftIcon)`
	rotate: 180deg;
`

const ArrowUpRight = styled(ArrowRightIcon)`
	rotate: -45deg;
`

const ContentWrapper = styled.span`
	display: flex;
	align-items: center;
	gap: 6px;
`

const RightAdornmentWrapper = styled.span`
	position: absolute;
	top: 0;
	right: 32px;
	height: 100%;
	display: flex;
	align-items: center;
`

const StyledBetaBadge = styled(BetaBadge)`
	margin-right: auto;
	margin-left: ${({ theme }) => theme.spaces.space3};
`

function FeatureLink({
	href,
	children,
	icon,
	isComingSoon,
	isBeta,
	disabled,
	rightAdornment,
	dataTestId,
	onClick,
}: Props) {
	const { withTemplateId } = useTemplateId()

	const content = (
		<ContentWrapper>
			{icon}
			{children}
		</ContentWrapper>
	)
	if (isComingSoon) {
		if (disabled) {
			return (
				<DisabledLink data-testid={dataTestId}>
					{content}
					<Badge>Coming soon!</Badge>
				</DisabledLink>
			)
		}

		return (
			<ExternalLink href={href} target="_blank" rel="noopener" data-testid={dataTestId} onClick={onClick}>
				{content}
				<Badge>
					Coming soon! <ArrowUpRight size={16} />
				</Badge>
			</ExternalLink>
		)
	}
	return (
		<InternalLinkWrapper>
			{disabled ? (
				<DisabledLink>
					{content} <SideChevron />
				</DisabledLink>
			) : (
				<InternalLink href={withTemplateId(href as string)} onClick={onClick}>
					{content}
					{isBeta && <StyledBetaBadge />}
					<SideChevron />
				</InternalLink>
			)}
			{rightAdornment && <RightAdornmentWrapper>{rightAdornment}</RightAdornmentWrapper>}
		</InternalLinkWrapper>
	)
}

export default FeatureLink
